/**
 * Single source of truth for the origins the client talks to.
 *
 * On the web the app is served from the same origin as the Express API, so
 * every request can stay relative and nothing here changes behaviour. Inside a
 * native shell (Capacitor) the document origin becomes `capacitor://localhost`
 * (iOS) or `http://localhost` (Android) — a WebView-local origin with no
 * backend behind it — so requests have to be addressed to an explicit host.
 *
 * Three distinct origins hide behind "the base URL", and conflating them is a
 * bug:
 *
 *   API_BASE_URL  where the REST API lives. Empty on web so URLs stay
 *                 relative (byte-identical to what shipped before).
 *   SOCKET_URL    where Socket.IO lives. Same host as the API, but socket.io
 *                 needs a real origin rather than a relative prefix.
 *   WEB_BASE_URL  the public https origin a human sees. Used for links that
 *                 leave the app — invite links, magic-link callbacks. This is
 *                 never the WebView origin: a `capacitor://localhost/...`
 *                 invite link is useless to whoever receives it.
 *
 * Configure a native build with VITE_API_BASE_URL / VITE_WEB_BASE_URL.
 */

/** Origins a Capacitor/Ionic WebView serves the bundle from. */
const NATIVE_SHELL_PROTOCOLS = ["capacitor:", "ionic:", "file:"];

/** Public production origin — the fallback when a native build ships unconfigured. */
const PRODUCTION_ORIGIN = "https://www.vyzle.com";

/** Strip a trailing slash so `${BASE}${path}` never produces a double slash. */
function normalizeBase(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  return trimmed.replace(/\/+$/, "");
}

const documentOrigin =
  typeof window !== "undefined" ? window.location.origin : "";

/**
 * True when the bundle is running inside a native WebView rather than a
 * browser tab. Detected from the document protocol so it stays correct
 * without a Capacitor dependency.
 *
 * Android's Capacitor shell serves from `http://localhost` — indistinguishable
 * from a dev server by protocol alone — so that case relies on
 * VITE_API_BASE_URL being set at build time instead.
 */
export const isNativeShell =
  typeof window !== "undefined" &&
  NATIVE_SHELL_PROTOCOLS.includes(window.location.protocol);

/**
 * True only in a bundle built for the native shell. Set at build time by
 * `mobile/`'s build script.
 *
 * The protocol sniff above cannot see Android: its Capacitor shell serves from
 * `https://localhost`, which is indistinguishable from a dev server. Anything
 * that must behave differently on *both* platforms — skipping the service
 * worker, hiding the install prompt, opening on /app instead of the marketing
 * page — has to key off this flag rather than the protocol.
 */
export const isNativeBuild = import.meta.env.VITE_NATIVE_SHELL === "true";

/** Either signal: built for native, or observably running in a WebView. */
const isNative = isNativeBuild || isNativeShell;

const configuredApiBase = normalizeBase(import.meta.env.VITE_API_BASE_URL);
const configuredWebBase = normalizeBase(import.meta.env.VITE_WEB_BASE_URL);

if (isNative && !configuredApiBase && import.meta.env.DEV) {
  console.warn(
    "[config] Running in a native shell without VITE_API_BASE_URL; " +
      `falling back to ${PRODUCTION_ORIGIN}.`,
  );
}

/**
 * Prefix for REST calls. Empty string on the web, which keeps every request
 * relative exactly as before — no absolute URLs, no preflights, no change to
 * what the service worker sees.
 */
export const API_BASE_URL: string =
  configuredApiBase ?? (isNative ? PRODUCTION_ORIGIN : "");

/**
 * Origin for the Socket.IO connection. socket.io-client wants an origin, not a
 * relative prefix, so this resolves to the current origin on the web.
 */
export const SOCKET_URL: string = API_BASE_URL || documentOrigin;

/**
 * Public origin for user-facing links that are shared or opened outside the
 * app. Falls back to the API origin, then to the current page's origin.
 */
export const WEB_BASE_URL: string =
  configuredWebBase ??
  (isNative ? configuredApiBase ?? PRODUCTION_ORIGIN : documentOrigin);

/** Build an absolute-or-relative URL for a REST endpoint: apiUrl("/api/rooms"). */
export function apiUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

/** Build a shareable public URL: webUrl(`/app/join/${code}`). */
export function webUrl(path: string): string {
  return `${WEB_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}
